import React, { useCallback, useState } from 'react';
import { useDocumentState } from '../../../contexts/DocumentContext';
import { useApolloClient, useQuery } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { DocumentWizardHeaderContainerProps } from './document-wizard-header.types';
import { DocumentWizardHeader } from './DocumentWizardHeader';
import { useDocumentRole } from '../../../shared/hooks/useDocumentRole/useDocumentRole';
import { DocumentStatusEnum } from '../../../shared/constants/document';
import { downloadPdfHelper } from '../../document-table/helpers/downloadPdfHelper';
import classNames from 'classnames';
import DocumentWizardHeaderDownloadSignedPDFButton from './document-wizard-header-download-signed-PDF-button/DocumentWizardHeaderDownloadSignedPDFButton';
import DocumentWizardHeaderUnshareButton from './document-wizard-header-unshare-button/DocumentWizardHeaderUnshareButton';
import { SendSigningReminder } from './components/headerActions/sendSigningReminder';
import { CancelExternalSigning } from './components/headerActions/cancelExternalSigning';
import { CancelPreparingToSign } from './components/headerActions/cancelPreparingToSign';
import { SendToExternalSigning } from './components/headerActions/sendToExternalSigning';
import { ExecutePageActions } from './components/ExecutePageActions';
import { unsendDocument } from '../../../utils/api/unsend-document-offer/unsend-document';
import ConfirmActionModal from '../../confirm-action-modal';
import { FormattedMessage } from 'react-intl';
import {
  DocumentSourceEnum,
  givePossessionSuccessMessage,
  successSendDocumentMessage,
} from '../document-editor/constants/EditorConstats';
import RequestReviewContainer from './components/headerActions/requestReview/RequestReviewContainer';
import { queriesV2 } from '../../../api';
import { DocumentLoadingSpinner } from '../document-loading-spinner/DocumentLoadingSpinner';
import {
  ApprovalRequestStatus,
  ApprovalRequestThreshold,
} from './components/headerActions/requestReview/RequestInternalApprovalModalConstants';
import { useAuth } from '../../../auth';
import HandleInternalApprovalActionsContainer from './components/headerActions/requestReview/HandleInternalApprovalActions';
import { InternalApprovalBadgeContainer } from './components/headerActions/requestReview/InternalApprovalBadgeContainer';
import { CancelApprovalButton } from './components/headerActions/requestReview/CancelApprovalButton';
import SendInternalReminderModal from './components/headerActions/sendInternalReminder/sendInternalReminderModal';
import { SigningProcessBadgeContainer } from './components/headerActions/sendToPreparingToSignInternal/signingProcessBadgeContainer';
import { useDocumentTeamPermissions } from '../../../pages/document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { givePossessionToCounterparty } from '../../../utils/api/give-possession-to-counterparty';
import { saveDocumentVersionOnPossessionPass } from './document-wizard-header.utils';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../../routing';
import { useDeal } from '../../../contexts/DealContext';
import { useNotifications } from '../../../notifications';
import { useIntl } from 'react-intl';
import { useSendDropdownLogic } from '../../../shared/hooks/useSendDropdownLogic/useSendDropdownLogic';
import { ApprovalStamp } from './components/ApprovalStamp';
import { ReactComponent as ChatIcon } from '../../../shared/icons/chat-icon.svg';
import { ReactComponent as DocumentCheckIcon } from '../../../shared/icons/document-check-icon.svg';
import { AiChatSlideout } from '../../ai-chat/AiChatSlideout';
import { ExtractedTermsSlideout } from '../../extractedTermsSlideout';
import { getDocumentById } from '../../../api/queries_v2';

export const DocumentWizardHeaderContainer = ({
  onClose,
  isViewingCompare,
  onExitCompare,
  title,
  titleBadge,
  canEdit,
  permissions,
  onSaveTitle,
  lastSaved,
  canShare,
  documentId,
  onSave,
  isDocumentLocked = false,
  dealId,
  teamId,
  documentSource = DocumentSourceEnum.APP,
  canRequestInternalApproval,
  isApprovalMode = false,
}: DocumentWizardHeaderContainerProps) => {
  const apolloClient = useApolloClient();
  const { state, updateDocumentState } = useDocumentState();
  const navigate = useNavigate();
  const [, { error: notifyError, success: notifySuccess }] = useNotifications();
  const { formatMessage } = useIntl();
  const [{ user }] = useAuth();
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const [isExtractedDataOpen, setIsExtractedDataOpen] = React.useState(false);
  const [showInternalReminderModal, setShowInternalReminderModal] = useState(false);
  const [isUnsendModalOpen, setIsUnsendModalOpen] = useState(false);
  const [isUnsendLoading, setIsUnsendLoading] = useState(false);

  // Query document data to get extractedDocumentTerm
  const { data: documentData } = useQuery(getDocumentById, {
    variables: { documentId },
    skip: !documentId,
  });
  const { isDocumentCreator, hasCurrentTeamPossession, isDocumentRoleLoading } = useDocumentRole(documentId);
  const isEditorLoadedState = state.isEditorLoaded;
  const approvalRequest = state.approvalRequest;
  const document = state.document;
  const signers = state.signers;
  const isDocumentEditable = state.isEditable;
  const sessionActivities = state.sessionActivities;
  const counterpartyPermissionLevel = state.counterpartyPermissionLevel;
  const isDocumentTitleEditable = state.isTitleEditable;
  const isEditorLoaded = documentSource === DocumentSourceEnum.APP ? isEditorLoadedState : true;
  const eversignPdfUrl = state.eversignPdfUrl;
  const signedPdfUrl = state.signedPdfUrl;
  const documentStatus = state.status;
  const documentTitle = state.title || 'Document';
  const hasEditPermission = !!permissions?.hasEditPermission;
  const isDraft = documentStatus === DocumentStatusEnum.DRAFT;
  const isSigning = documentStatus === DocumentStatusEnum.SIGNING;
  const isReviewing = documentStatus === DocumentStatusEnum.REVIEWING;
  const isUploaded = documentStatus === DocumentStatusEnum.UPLOADED;
  const isExecuted = documentStatus === DocumentStatusEnum.EXECUTED;
  const isShared = documentStatus === DocumentStatusEnum.SHARED;
  const canBeEditorModeChanged = isDraft || isReviewing;
  const { currentDeal } = useDeal();
  const isDealShared = !!currentDeal?.receiverEmail;
  const documentSourceFromSelector = state.source;

  const isPreparingToSign = documentStatus === DocumentStatusEnum.PREPARING_TO_SIGN;
  const shouldShowCancelPreparingToSign =
    isPreparingToSign && isDocumentCreator && hasEditPermission && !isDocumentLocked;
  const shouldShowSendToExternalSigning =
    isPreparingToSign && isDocumentCreator && hasEditPermission && !isDocumentLocked;
  const shouldShowInternalSendForSigning =
    isDocumentCreator &&
    hasEditPermission &&
    (isDraft || isReviewing || isUploaded || isShared) &&
    hasCurrentTeamPossession &&
    isEditorLoaded;
  const { hasInviteSendingPermission } = useDocumentTeamPermissions({ teamId, apolloClient });
  const dealUrl = paths.deals.detail.replace(':teamId', teamId as string).replace(':dealId', dealId);
  const { possessionTeamName } = useDocumentRole(documentId);

  const _handleSendDocumentMutation = useCallback(async () => {
    const isAppDocument = documentSource === DocumentSourceEnum.APP;

    if (isAppDocument) {
      onSave && (await onSave());
      await saveDocumentVersionOnPossessionPass({ success: true, possessionTeamName });
    }

    const additionalMessage = '';

    givePossessionToCounterparty({
      documentId,
      additionalMessage,
      sessionActivities,
      counterpartyPermissionLevel,
    })(apolloClient)
      .then(() => {
        updateDocumentState({ sessionActivities: [] });
        navigate(dealUrl);
        isDealShared &&
          notifySuccess({
            message: formatMessage(
              documentStatus === DocumentStatusEnum.DRAFT ? successSendDocumentMessage : givePossessionSuccessMessage
            ),
          });
      })
      .catch((error) => {
        if (isAppDocument) {
          saveDocumentVersionOnPossessionPass({ success: false });
        }

        notifyError({ message: error.message });
      });
  }, [
    apolloClient,
    dealUrl,
    documentId,
    documentSource,
    documentStatus,
    formatMessage,
    onSave,
    notifyError,
    notifySuccess,
    possessionTeamName,
    // givePossessionToCounterparty is a mutation function, not a dependency
    isDealShared,
    sessionActivities,
    counterpartyPermissionLevel,
    updateDocumentState,
    navigate,
  ]);

  const shouldShowSignButton = () => {
    return !isDocumentCreator && hasEditPermission && isPreparingToSign;
  };

  const shouldShowDownloadPDFButton = isExecuted;

  const isApprovalRequestPending = approvalRequest?.status === ApprovalRequestStatus.PENDING;
  const isApprovalRequestApproved = approvalRequest?.status === ApprovalRequestStatus.APPROVED;

  const activeReview = approvalRequest?.reviews
    .filter((request: any) => {
      return !request.approvedAt && !request.rejectedAt;
    })
    .find((review: { reviewer: { id: string | number } }) => review?.reviewer?.id === user.id);

  const activeReviewId = activeReview?.id;

  // Check if current user has approved this document
  const userApprovedReview = approvalRequest?.reviews.find((review: any) => {
    return review.reviewer?.id === user.id && review.approvedAt;
  });

  // Check if user is an approver with edit permissions during approval
  const userIsApprover = !!activeReview;
  const approverHasEditPermissions = userIsApprover && approvalRequest?.permitEditingDuringApproval;

  const { loading: dealTeamsQueryLoading, data: dealTeamsData } = useQuery(queriesV2.getDealTeamsByTeamId, {
    variables: {
      dealId,
    },
  });

  const { data: documentTeamsAdminData } = useQuery(queriesV2.getDocumentById, {
    variables: {
      documentId,
    },
  });

  const shouldShowCancelSigningButton = () => {
    if (!isDocumentCreator) return false;

    return hasEditPermission && isDocumentCreator && isSigning;
  };

  const shouldShowSendReminderButton = () => {
    return shouldShowCancelSigningButton() && signers?.filter((signer) => !signer?.signedAt)?.length;
  };

  // Check if document has receiver team to show Unshare button
  const documentReceiverTeam = documentTeamsAdminData?.getDocumentById?.receiverTeam;
  const hasDocumentReceiverTeam = !!documentReceiverTeam;
  // Only show Unshare button for executed documents with a receiver team
  const shouldShowUnshareButton =
    isDocumentCreator &&
    hasInviteSendingPermission &&
    hasDocumentReceiverTeam &&
    isExecuted;

  // Use the extracted business logic hook BEFORE any conditional returns
  const { shouldShowRequestReviewContainer, shouldShowSendForSigning } = useSendDropdownLogic({
    isDocumentCreator,
    hasCurrentTeamPossession,
    canShare,
    canEdit,
    canRequestInternalApproval,
    isReviewing,
    isDocumentEditable,
    documentSource,
    approvalRequest,
    shouldShowInternalSendForSigning,
    isPreparingToSign,
    isExecuted,
  });

  if (isDocumentRoleLoading || dealTeamsQueryLoading) {
    return <DocumentLoadingSpinner />;
  }

  const handleDownloadSignedPDF = async () => {
    const pdfUrl = signedPdfUrl || eversignPdfUrl;
    try {
      await downloadPdfHelper(pdfUrl, documentTitle);
    } catch {
      notifyError({ message: 'Failed to download document. Please try again.' });
    }
  };

  const handleUnsendModalClose = () => {
    setIsUnsendModalOpen(false);
  };

  const handleUnsendConfirm = async () => {
    setIsUnsendLoading(true);
    try {
      await unsendDocument({ documentId, dealId })(apolloClient);
      // Refetch queries to update UI with latest data
      await apolloClient.refetchQueries({
        include: ['GetDocumentById', 'GetDocumentsByDeal'],
      });
      // Show success notification
      setTimeout(() => {
        notifySuccess({
          message: formatMessage({
            id: 'unsend-document.success',
            defaultMessage: 'Document unshared successfully. Counterparty access has been removed.',
          }),
        });
      }, 100);
      setIsUnsendModalOpen(false);
    } catch (error) {
      console.error('Error unsending document:', error);
      notifyError({
        message: formatMessage({
          id: 'unsend-document.error',
          defaultMessage: 'Failed to unshare document. Please try again.',
        }),
      });
    } finally {
      setIsUnsendLoading(false);
    }
  };

  const getHeaderActions = () => (
    <>
      {/* Extracted Data Button */}
      {documentData?.getDocumentById?.extractedDocumentTerm && isDocumentCreator && (
        <button
          type="button"
          className="document-wizard-header__extracted-data-button"
          onClick={() => setIsExtractedDataOpen(true)}
          title="View extracted data"
          aria-label="Open extracted data"
        >
          <DocumentCheckIcon />
        </button>
      )}
      {/* AI Chat Button */}
      <button
        type="button"
        className="document-wizard-header__chat-button"
        onClick={() => setIsChatOpen(true)}
        title="Chat with AI about this document"
        aria-label="Open AI Chat"
      >
        <ChatIcon />
      </button>
      {shouldShowRequestReviewContainer && (
        <RequestReviewContainer
          dealTeamsData={dealTeamsData}
          documentTeamsAdminData={documentTeamsAdminData}
          documentId={documentId}
          dealId={dealId}
          handleSaveDocument={onSave}
          teamId={teamId}
          isDocumentEditable={isDocumentEditable}
          isDocumentCreator={isDocumentCreator}
          isDocumentShared={isShared}
          shouldShowInternalSendForSigning={!!shouldShowInternalSendForSigning}
          hasInviteSendingPermission={hasInviteSendingPermission}
          hasEditPermission={hasEditPermission}
          isDraft={isDraft}
          isUploaded={isUploaded}
          isReviewing={isReviewing}
          isShared={isShared}
          isExecuted={isExecuted}
        />
      )}
      {/* Actions dropdown for signing documents only */}
      {isSigning && (
        <ExecutePageActions
          documentId={documentId}
          dealId={dealId}
          teamId={teamId}
          documentTitle={documentTitle}
          hasEditPermission={hasEditPermission}
        />
      )}
      {/* Individual action buttons for non-signing documents */}
      {shouldShowSendReminderButton() && !isSigning && !isExecuted && (
        <SendSigningReminder
          dealId={dealId}
          teamId={teamId}
          className={classNames({
            'document-wizard-header__edit-button_margin': shouldShowCancelSigningButton() && shouldShowSignButton,
          })}
        />
      )}
      {shouldShowCancelSigningButton() && !isSigning && !isExecuted && (
        <CancelExternalSigning
          dealId={dealId}
          teamId={teamId}
          className={classNames({
            'document-wizard-header__edit-button_margin': shouldShowCancelSigningButton() && shouldShowSignButton,
          })}
        />
      )}
      {shouldShowUnshareButton && (
        <DocumentWizardHeaderUnshareButton onClick={() => setIsUnsendModalOpen(true)} />
      )}
      {shouldShowDownloadPDFButton && (
        <DocumentWizardHeaderDownloadSignedPDFButton onClick={handleDownloadSignedPDF} />
      )}
      {shouldShowCancelPreparingToSign && <CancelPreparingToSign />}
      {shouldShowSendToExternalSigning && (
        <SendToExternalSigning documentId={documentId} dealId={dealId} teamId={teamId} />
      )}
    </>
  );

  const internalApprovalBadge = () => {
    const userIsCreator =
      approvalRequest?.creator && typeof approvalRequest.creator === 'object' && 'id' in approvalRequest.creator
        ? approvalRequest.creator.id === user.id
        : false;
    const currentApproverId = (approvalRequest?.currentApprover as any)?.id;

    return (
      <InternalApprovalBadgeContainer
        documentId={documentId}
        dealId={dealId}
        requestId={approvalRequest!.id}
        teamId={teamId}
        approvalRequestedReviews={approvalRequest?.reviews}
        isOneConfirmationRequired={approvalRequest?.threshold === ApprovalRequestThreshold.ONE}
        useApprovalOrder={Boolean(approvalRequest?.useApprovalOrder)}
        currentApproverId={currentApproverId}
        isCreator={userIsCreator}
      />
    );
  };

  const signingProcessBadge = () => (
    <SigningProcessBadgeContainer signers={signers} useSignerOrder={Boolean((document as any)?.useSignerOrder)} />
  );

  const getHeaderActionsForDocumentWithPendingInternalApproval = () => {
    const userIsApprovalCreator =
      approvalRequest?.creator && typeof approvalRequest.creator === 'object' && 'id' in approvalRequest.creator
        ? approvalRequest.creator.id === user.id
        : false;

    return (
      <>
        {activeReviewId && (
          <HandleInternalApprovalActionsContainer
            dealTeamsData={dealTeamsData}
            documentId={documentId}
            dealId={dealId}
            reviewId={activeReviewId}
            teamId={teamId}
            handleSaveDocument={onSave}
          />
        )}
        {userIsApprovalCreator && (
          <>
            <Button
              variant="primary"
              onClick={() => setShowInternalReminderModal(true)}
              className="document-wizard-header__edit-button_margin"
              style={{ marginRight: '0.5rem' }}
            >
              Send Reminder
            </Button>
            <CancelApprovalButton
              documentId={documentId}
              dealId={dealId}
              teamId={teamId}
              requestId={approvalRequest!.id}
              className="document-wizard-header__edit-button_margin"
            />
          </>
        )}
      </>
    );
  };

  return (
    <>
      <DocumentWizardHeader
      // Drops the header's bottom divider for the DOCX-native editor, so the
      // header and the editor toolbar read as one continuous bar (the template
      // header does the same via `is-docx-native`).
      isDocxNative={
        (documentData?.getDocumentById?.editorConfiguration as Record<string, any> | null)
          ?.editor_engine === 'docx_native'
      }
      shouldShowEditorModeSwitcherActive={canBeEditorModeChanged}
      isUploaded={isUploaded}
      onClose={onClose}
      isViewingCompare={isViewingCompare}
      onExitCompare={onExitCompare}
      title={title}
      titleBadge={isApprovalRequestPending ? internalApprovalBadge() : isSigning ? signingProcessBadge() : titleBadge}
      onSaveTitle={onSaveTitle}
      canEdit={canEdit && isDocumentTitleEditable}
      hasUserEditPermission={hasEditPermission}
      lastSaved={lastSaved}
      isReviewing={isReviewing}
      hasInviteSendingPermission={hasInviteSendingPermission}
      dealId={dealId}
      teamId={teamId}
      documentId={documentId}
      actions={isApprovalRequestPending ? getHeaderActionsForDocumentWithPendingInternalApproval() : getHeaderActions()}
      dealTeamsData={dealTeamsData}
      onSave={onSave}
      canShare={canShare}
      shouldShowInternalSendForSigning={shouldShowInternalSendForSigning}
      shouldShowSendForSigning={shouldShowSendForSigning}
      isApprovalMode={isApprovalMode}
      isApproverWithEditPermissions={approverHasEditPermissions}
      hideLeftNav={approverHasEditPermissions}
      isPreparingToSign={isPreparingToSign}
      isSigning={isSigning}
      isExecuted={isExecuted}
      approvalStamp={
        userApprovedReview && !isPreparingToSign && !isSigning && isApprovalRequestApproved
          ? <ApprovalStamp approvedAt={userApprovedReview.approvedAt} />
          : undefined
      }
      isApprovalRequestApproved={isApprovalRequestApproved}
      documentSource={documentSourceFromSelector || documentSource}
    />
    {isChatOpen && (
      <AiChatSlideout
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        documentId={documentId}
        documentName={documentTitle}
        teamId={teamId}
        dealId={dealId}
      />
    )}
    {isExtractedDataOpen && documentData?.getDocumentById?.extractedDocumentTerm && isDocumentCreator && (
      <ExtractedTermsSlideout
        isOpen={isExtractedDataOpen}
        onClose={() => setIsExtractedDataOpen(false)}
        documentId={documentId}
        documentName={documentTitle}
        extractedTerm={documentData.getDocumentById.extractedDocumentTerm as any}
        canEdit={hasEditPermission}
        dealId={dealId}
      />
    )}
    {showInternalReminderModal && approvalRequest && (
      <SendInternalReminderModal
        show={showInternalReminderModal}
        onHide={() => setShowInternalReminderModal(false)}
        documentId={documentId}
        approvalRequestId={approvalRequest.id}
        reviewers={
          approvalRequest.useApprovalOrder && approvalRequest.currentApprover
            ? [] // For sequential approval, we only use currentReviewer
            : approvalRequest.reviews?.filter((r: any) => !r.approvedAt && !r.rejectedAt).map((r: any) => ({
                id: r.reviewer.id,
                fullName: r.reviewer.fullName,
                email: r.reviewer.email
              }))
        }
        useApprovalOrder={Boolean(approvalRequest.useApprovalOrder)}
        currentReviewer={approvalRequest.currentApprover &&
          typeof approvalRequest.currentApprover === 'object' &&
          'fullName' in approvalRequest.currentApprover ? {
          fullName: (approvalRequest.currentApprover as any).fullName,
          email: (approvalRequest.currentApprover as any).email
        } : undefined}
      />
    )}
    <ConfirmActionModal
      show={isUnsendModalOpen}
      onHide={handleUnsendModalClose}
      onClick={handleUnsendConfirm}
      title={<FormattedMessage id="unsend-document.modal.title" defaultMessage="Unshare Document" />}
      message={
        <FormattedMessage
          id="unsend-document.modal.message"
          defaultMessage="Are you sure you want to unshare this document? The counterparty will lose access and the document will return to your possession."
        />
      }
      action={
        isUnsendLoading ? (
          <FormattedMessage id="unsend-document.modal.action.loading" defaultMessage="Unsharing..." />
        ) : (
          <FormattedMessage id="unsend-document.modal.action" defaultMessage="Unshare" />
        )
      }
    />
    </>
  );
};
